<template>
    <div class="modal" :style="{ zIndex: 1000 }">
        <div class="modal-content">
            <h3>{{ isEditing ? 'Edit Event' : 'Add New Event' }}</h3>
            <form @submit.prevent="handleSubmit">
                <div class="top-row">
                    <div class="form-group">
                        <label>Event Type</label>
                        <select v-model="eventForm.event_type" required>
                            <option value="Regular Meeting">Regular Meeting</option>
                            <option value="Network Event">Network Event</option>
                            <option value="Social Event">Social Event</option>
                        </select>
                    </div>

                    <div class="form-group" v-if="eventForm.event_type === 'Regular Meeting'">
                        <label>Format</label>
                        <select v-model="eventForm.format_id" required>
                            <option value="">Select a format...</option>
                            <option v-for="format in formats" :key="format.id" :value="format.id">
                                {{ format.name }}
                            </option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label>Venue</label>
                        <select v-model="eventForm.venue_id" required>
                            <option value="">Select a venue</option>
                            <option v-for="venue in venues" 
                                    :key="venue.id" 
                                    :value="venue.id">
                                {{ venue.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Event Details -->
                <div class="form-section">
                    <h4>Event Details</h4>
                    <div class="date-time-section">
                        <div class="start-section">
                            <label>Start</label>
                            <div class="datetime-inputs">
                                <input 
                                    type="date" 
                                    v-model="eventForm.start_date"
                                    required
                                >
                                <select 
                                    v-model="eventForm.start_time"
                                    required
                                >
                                    <option value="">Select time</option>
                                    <option 
                                        v-for="time in timeOptions" 
                                        :key="time"
                                        :value="time"
                                    >
                                        {{ time }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="end-section">
                            <label>End</label>
                            <div class="datetime-inputs">
                                <input 
                                    type="date" 
                                    v-model="eventForm.end_date"
                                    required
                                >
                                <select 
                                    v-model="eventForm.end_time"
                                    required
                                >
                                    <option value="">Select time</option>
                                    <option 
                                        v-for="time in timeOptions" 
                                        :key="time"
                                        :value="time"
                                    >
                                        {{ time }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Dynamic Session Fields based on format -->
                <div v-if="eventForm.event_type === 'Regular Meeting'" class="form-section">
                    <h4>Session Details</h4>
                    
                    <template v-if="selectedFormat && sessions.length === selectedFormat.segments.length">
                        <div v-for="(segment, index) in selectedFormat.segments" :key="index" class="session-block">
                            <h5 class="segment-header">{{ getSegmentTime(index) }} {{ segment.name }} ({{ selectedFormat.segments[index].duration_minutes }} minutes)</h5>
                            
                            <!-- For segments requiring a member speaker -->
                            <template v-if="segment.segment_type === 'member_speaker' || sessions[index].session_type === 'member_speaker'">
                                <div class="form-group text-left compact">
                                    <label>Speaker</label>
                                    <select 
                                        v-model="sessions[index].speaker_id" 
                                        required
                                    >
                                        <option :value="null">Select member...</option>
                                        <option 
                                            v-for="member in availableMembers(index)" 
                                            :key="member.id" 
                                            :value="member.id"
                                        >
                                            {{ member.name }}
                                        </option>
                                    </select>
                                </div>
                                <div class="form-group text-left compact">
                                    <label>Topic</label>
                                    <input type="text" v-model="sessions[index].topic" required>
                                </div>
                                <div class="form-group text-left compact">
                                    <label>Description</label>
                                    <textarea v-model="sessions[index].description" rows="2"></textarea>
                                </div>
                            </template>

                            <!-- For segments requiring an external speaker -->
                            <template v-if="segment.segment_type === 'external_speaker' || sessions[index].session_type === 'external_speaker'">
                                <div class="form-group text-left compact">
                                    <label>Speaker Name</label>
                                    <input type="text" v-model="sessions[index].guest_speaker_name" required>
                                </div>
                                <div class="form-group text-left compact">
                                    <label>Topic</label>
                                    <input type="text" v-model="sessions[index].topic" required>
                                </div>
                                <div class="form-group text-left compact">
                                    <label>Description</label>
                                    <textarea v-model="sessions[index].description" rows="2"></textarea>
                                </div>
                            </template>

                            <!-- For discussion segments -->
                            <template v-if="segment.segment_type === 'discussion'">
                                <div class="form-group text-left compact">
                                    <label>Discussion Topic</label>
                                    <input 
                                        type="text" 
                                        v-model="sessions[index].topic" 
                                        required
                                        placeholder="Enter the topic for discussion"
                                    >
                                </div>
                                <div class="form-group text-left compact">
                                    <label>Additional Details</label>
                                    <textarea 
                                        v-model="sessions[index].description" 
                                        rows="2"
                                        placeholder="Enter any additional details about the discussion"
                                    ></textarea>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>

                <div class="modal-actions">
                    <button type="button" @click="$emit('close')" class="cancel-btn">Cancel</button>
                    <button type="submit" class="save-btn">Save</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { eventService } from '../services/api';

export default {
    name: 'EventModal',
    // Props define the interface between parent (EventManagement) and this modal
    props: {
        // Controls whether the modal is in edit mode
        // When true, existing event data will be loaded and preserved
        // Used to determine form behavior and validation rules
        isEditing: Boolean,
        
        // Contains the complete event data when editing an existing event
        // Required fields for successful update:
        // - id: Original event ID for database update
        // - event_type: Type of event (e.g., 'Regular Meeting')
        // - format_id: Meeting format template ID
        // - sessions: Array of existing sessions with:
        //   - id: Original session ID
        //   - speaker_id: Assigned speaker's ID
        //   - session_type: Type of session segment
        //   - topic: Session topic (optional)
        //   - description: Session description (optional)
        editingEvent: {
            type: Object,
            default: null
        },
        // List of available venues for the event location
        venues: {
            type: Array,
            required: true
        },
        // List of members who can be assigned as speakers
        members: {
            type: Array,
            required: true
        },
        // Available meeting formats. Must be loaded by parent before showing modal.
        // Each format defines the structure of sessions for a meeting.
        formats: {
            type: Array,
            default: () => []  
        },
        // Initial data for the form
        initialData: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['save', 'close', 'update:members'],
    setup(props, { emit }) {
        // ==========================================
        // State Management
        // ==========================================
        
        // Main form state - holds all event data including sessions
        // In edit mode: populated from editingEvent prop
        // In create mode: initialized with default values
        const eventForm = ref({
            event_type: 'Regular Meeting',
            format_id: '',
            ...props.initialData
        });

        // Helper function to combine date and time strings into ISO format
        const combineDateTime = (date, time) => {
            if (!date || !time) return null;
                        
            // Parse the components
            const [hours, minutes] = time.split(':').map(Number);
            const [year, month, day] = date.split('-').map(Number);
            
            // Create local date object
            const localDate = new Date(year, month - 1, day, hours, minutes);
            
            // Single conversion to UTC
            const utcString = localDate.toISOString();
            
            return utcString;
        };

        // Generates time slot options for the time picker dropdown
        // Creates 15-minute intervals from 6:00 AM to 9:45 PM
        const timeOptions = computed(() => {
            const options = [];
            for (let hour = 6; hour <= 21; hour++) {
                for (let minute = 0; minute < 60; minute += 15) {
                    const formattedHour = hour.toString().padStart(2, '0');
                    const formattedMinute = minute.toString().padStart(2, '0');
                    options.push(`${formattedHour}:${formattedMinute}`);
                }
            }
            return options;
        });

        const speakingHistory = ref({});

        // Fetches speaking history for all members
        // Used to display last speaking date and count in member selection
        const loadSpeakingHistory = async () => {
            try {
                const history = await eventService.getMemberSpeakingHistory();
                speakingHistory.value = history;
            } catch (error) {
                // Error loading speaking history
            }
        };

        onMounted(() => {
            loadSpeakingHistory();
        });

        // Finds and returns the currently selected meeting format
        // Used to determine session structure and timing
        const selectedFormat = computed(() => {
            if (!props.formats) return null;
            return props.formats.find(format => format.id === eventForm.value.format_id) || null;
        });

        // Manages session data for both create and edit modes
        // Create mode:
        //   - Generates new sessions from format template
        //   - Sets default values for all session fields
        // Edit mode:
        //   - Preserves existing session data and IDs
        //   - Maintains speaker assignments and topics
        //   - Validates session count matches format
        const sessions = computed(() => {
            const format = selectedFormat.value;
            
            if (!format?.segments) {
                return [];
            }


            const usedSessions = new Set();

            // Map format segments to sessions
            const mappedSessions = format.segments.map((segment, index) => {
                // Use index directly for speaker order (1-based)
                const speakerOrder = index + 1;

                // Try to find an existing session for this segment
                const existingSession = eventForm.value.sessions?.find(s => {
                    // For any session type we care about (member_speaker, external_speaker, discussion)
                    if ((s.session_type === 'member_speaker' || 
                         s.session_type === 'external_speaker' || 
                         s.session_type === 'discussion')) {
                        
                        // Match by type and speaker_order
                        const typeMatch = s.session_type === segment.segment_type;
                        const orderMatch = s.speaker_order === speakerOrder;
                        const notUsed = !usedSessions.has(s.id);
                        
                        const matches = typeMatch && orderMatch && notUsed;
                        if (matches && s.id) {
                            usedSessions.add(s.id);
                        }
                        return matches;
                    }
                    return false;
                });

                if (existingSession) {
                    return existingSession;
                }

                return {
                    id: null,
                    session_type: segment.segment_type,
                    speaker_id: segment.segment_type === 'member_speaker' ? null : undefined,
                    topic: '',
                    description: '',
                    guest_speaker_name: segment.segment_type === 'external_speaker' ? '' : undefined,
                    speaker_order: speakerOrder
                };
            });

            return mappedSessions;
        });

        // Helper function to calculate days since last spoke
        const getDaysSinceLastSpoke = (lastSpokeDate) => {
            if (!lastSpokeDate) return null;
            
            const lastSpoke = new Date(lastSpokeDate);
            const today = new Date();
            const diffTime = Math.abs(today - lastSpoke);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            return diffDays;
        };

        const availableMembers = (sessionIndex) => {
            if (!eventForm.value.start_date) return [];
                        
            // Get all members except those already assigned to other sessions
            const assignedMembers = new Set(
                sessions.value
                    .filter((s, i) => i !== sessionIndex && s.speaker_id)
                    .map(s => s.speaker_id)
            );
            
            const currentSession = sessions.value[sessionIndex];
            
            const members = props.members
                .filter(m => !assignedMembers.has(m.id) || m.id === currentSession?.speaker_id)
                .map(member => {
                    const history = speakingHistory.value[member.id];
                    const daysSince = history?.last_spoke ? getDaysSinceLastSpoke(history.last_spoke) : null;
                    const lastSpokeDisplay = daysSince ? `${daysSince} days ago` : 'Never';
                    
                    return {
                        id: member.id,
                        name: `${member.first_name} ${member.last_name} (Last spoke: ${lastSpokeDisplay}, Count: ${history?.count || 0})`
                    };
                });
            
            return members;
        };

        // Handles initialization of form data when editing an existing event
        // Process flow:
        // 1. Processes session data:
        //    - Converts string speaker_ids to numbers
        //    - Ensures required fields have default values
        //    - Preserves existing session IDs for update
        // 2. Updates form with event data:
        //    - Maintains all existing fields and relationships
        //    - Sets default values for missing fields
        // 3. Refreshes speaking history for member selection
        watch(() => props.editingEvent, (newEvent) => {
            if (newEvent) {
                
                // Initialize variables
                let startDate, startTime, endDate, endTime;
                
                // Convert UTC to local time
                const localDate = new Date(newEvent.event_date);
                
                // Get date in YYYY-MM-DD format without timezone conversion
                startDate = localDate.getFullYear() + '-' +
                    String(localDate.getMonth() + 1).padStart(2, '0') + '-' +
                    String(localDate.getDate()).padStart(2, '0');
                    
                // Get time in local timezone
                startTime = localDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
                
                if (newEvent.end_time) {
                    
                    // Convert UTC to local time
                    const localEndDate = new Date(newEvent.end_time);
                    
                    // Get date in YYYY-MM-DD format without timezone conversion
                    endDate = localEndDate.getFullYear() + '-' +
                        String(localEndDate.getMonth() + 1).padStart(2, '0') + '-' +
                        String(localEndDate.getDate()).padStart(2, '0');
                        
                    // Get time in local timezone
                    endTime = localEndDate.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });                    
                }

                // Ensure sessions have the correct speaker_order
                const processedSessions = newEvent.sessions.map(session => {

                    // Find matching segment to get correct order_index
                    const matchingSegment = selectedFormat.value?.segments?.find(
                        segment => segment.segment_type === session.session_type
                    );

                    // If we have a matching segment, use its order_index, otherwise keep existing speaker_order
                    const updatedSession = {
                        ...session,
                        speaker_order: matchingSegment ? matchingSegment.order_index : session.speaker_order
                    };

                    return updatedSession;
                });

                
                eventForm.value = {
                    event_type: newEvent.event_type || 'Regular Meeting',
                    format_id: newEvent.format_id,
                    start_date: startDate || newEvent.start_date,
                    start_time: startTime || newEvent.start_time,
                    end_date: endDate || newEvent.end_date,
                    end_time: endTime || newEvent.end_time,
                    venue_id: newEvent.venue_id,
                    sessions: processedSessions
                };

            }
        }, { immediate: true });

        // Handles changes to the selected meeting format
        // - Clears sessions if format is removed
        // - Preserves existing sessions in edit mode
        // - Creates new sessions from format template in create mode
        // - Updates speaking history when format changes
        watch(() => eventForm.value.format_id, async (newFormatId) => {
            if (!newFormatId) return;

            const format = props.formats.find(f => f.id === newFormatId);
            
            // Initialize sessions array if it doesn't exist
            if (!eventForm.value.sessions) {
                eventForm.value.sessions = [];
            }

            // Only reset sessions if we're not editing
            if (!props.isEditing) {
                // Map format segments to new sessions
                const segments = format.segments || [];
                eventForm.value.sessions = segments
                    .filter(segment => 
                        segment.segment_type === 'member_speaker' || 
                        segment.segment_type === 'external_speaker'
                    )
                    .map(segment => ({
                        id: null,
                        session_type: segment.segment_type,
                        speaker_id: null,
                        topic: '',
                        description: '',
                        speaker_order: segment.order_index,
                        guest_speaker_name: null
                    }));
            }
            
            // Load speaking history when format changes
            loadSpeakingHistory();
        });

        watch(() => eventForm.value.sessions, () => {
        }, { deep: true });

        // Formats speaking history for display in member selection
        // Shows last speaking date and total speaking count
        const getSpeakingHistory = (member) => {
            const history = speakingHistory.value[member.id];
            if (!history) return 'No history';
            
            const daysSince = history.last_spoke ? getDaysSinceLastSpoke(history.last_spoke) : null;
            const lastSpokeDisplay = daysSince ? `${daysSince} days ago` : 'Never';
            
            return `Last spoke: ${lastSpokeDisplay}, Count: ${history.count || 0}`;
        };

        // Calculates the start time for a specific session segment
        // Takes into account duration of all previous segments based on format
        const getSegmentTime = (index) => {
            if (!eventForm.value.start_time) return '';
            
            let totalMinutes = 0;
            for (let i = 0; i < index; i++) {
                totalMinutes += selectedFormat.value.segments[i].duration_minutes;
            }
            
            const [hours, minutes] = eventForm.value.start_time.split(':').map(Number);
            const segmentStartTime = new Date();
            segmentStartTime.setHours(hours);
            segmentStartTime.setMinutes(minutes + totalMinutes);
            
            return segmentStartTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        };

        // Updates end date to match start date (most events end same day)
        // Fetches updated member availability for the selected date
        watch(() => eventForm.value.start_date, async (newDate) => {
            eventForm.value.end_date = newDate;
            if (newDate) {
                const membersResponse = await eventService.getAllMembers(newDate);
                emit('update:members', membersResponse);
            }
        });

        // Automatically sets end time 90 minutes after start time
        // This is the default duration for most meetings
        watch(() => eventForm.value.start_time, (newTime) => {
            // Only auto-set end time for new events, not when editing
            if (newTime && !props.isEditing) {
                const [hours, minutes] = newTime.split(':').map(Number);
                const endTime = new Date();
                endTime.setHours(hours);
                endTime.setMinutes(minutes + 90);
                
                eventForm.value.end_time = endTime.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }).substring(0, 5);
            }
        });

        const handleSubmit = async () => {
            try {
                // Validate required fields
                if (!eventForm.value.start_date || !eventForm.value.start_time) {
                    throw new Error('Event date and time are required');
                }

                const formData = {
                    event_type: eventForm.value.event_type,
                    format_id: eventForm.value.format_id || null,
                    venue_id: eventForm.value.venue_id,
                    event_date: combineDateTime(eventForm.value.start_date, eventForm.value.start_time),
                    end_time: combineDateTime(eventForm.value.end_date, eventForm.value.end_time),
                    sessions: sessions.value
                        .filter(session => 
                            session.session_type === 'member_speaker' ||
                            session.session_type === 'external_speaker' ||
                            session.session_type === 'discussion'
                        )
                        .map(session => ({
                            id: session.id,
                            session_type: session.session_type,
                            speaker_id: session.speaker_id,
                            topic: session.topic,
                            description: session.description,
                            guest_speaker_name: session.guest_speaker_name,
                            speaker_order: session.speaker_order
                        }))
                };

                emit('save', {
                    data: formData,
                    isEditing: props.isEditing,
                    editingEventId: props.editingEvent?.id
                });
                emit('close');
            } catch (error) {
                // Don't close modal if there's an error
                return;
            }
        };

        return {
            eventForm,
            timeOptions,
            selectedFormat,
            sessions,
            handleSubmit,
            getSegmentTime,
            availableMembers,
            getSpeakingHistory,
            getDaysSinceLastSpoke
        };
    }
};
</script>

<style scoped>
/* Copy all styles from EventManagement.vue that are related to the modal */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    overflow-y: auto;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 800px;
    position: relative;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    text-align: left;
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

.form-section {
    margin-top: 2rem;
    padding: 1.5rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.form-section h4 {
    margin-top: 0;
    margin-bottom: 1.5rem;
    color: #2c3e50;
}

.session-block {
    background: #f8f9fa;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 4px;
}

.segment-header {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    text-align: left;
    font-weight: normal;
}

.form-group.compact {
    margin-bottom: 0.5rem;
}

.form-group.compact label {
    margin-bottom: 0.25rem;
}

.form-group.compact input,
.form-group.compact select,
.form-group.compact textarea {
    padding: 0.25rem 0.5rem;
}

.form-group.compact textarea {
    min-height: 2.5rem;
}

.date-time-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.start-section,
.end-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.datetime-inputs {
    display: grid;
    grid-template-columns: 1fr 120px;
    gap: 0.5rem;
}

.datetime-inputs input,
.datetime-inputs select {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.top-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
}

.text-left {
    text-align: left;
}

.modal-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.save-btn,
.cancel-btn {
    padding: 0.5rem 1.5rem;
    border-radius: 4px;
    border: none;
    font-size: 1rem;
    cursor: pointer;
}

.save-btn {
    background-color: #4CAF50;
    color: white;
}

.save-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.cancel-btn {
    background-color: #f8f9fa;
    border: 1px solid #ddd;
}
</style>